import { FinalTournamentView } from '../components/scoring/FinalTournamentView';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { EventDayBar } from '../components/scoring/EventDayBar';
import { pushScoringToast } from '../components/common/admin-toasts';
import { ApiError, apiFetch } from '../lib/api';
import { useScoringTournament } from '../hooks/useScoringTournament';
import { useEventDayToggle } from '../hooks/useEventDayToggle';
import { scoringListPath } from '../lib/scoring-routes';
import { scoringTitle } from '../lib/page-title';
import { round1TeamsEnabled, type Round1AdminView } from '../lib/round1';
import { SHOOTER_GENDER_LABELS } from '../lib/shooting';

const fieldClass =
  'h-12 w-full rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] px-3 text-base text-[var(--color-text-primary)]';

/** Round 1 teams, the shot format, and the cut into Round 2. Admins only. */
export function Round1TeamsPage() {
  const { slug: slugParam } = useParams();
  const { slug, detail, setDetail, notFound, error } = useScoringTournament(slugParam);
  const tournamentId = detail?.tournament.id;
  const enabled = detail ? round1TeamsEnabled(detail.tournament) : false;
  const { eventDay, busy, error: dayError, toggle } = useEventDayToggle(detail, setDetail);
  const [view, setView] = useState<Round1AdminView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rename, setRename] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    document.title = scoringTitle('Teams', detail?.tournament.title);
  }, [detail?.tournament.title]);

  useEffect(() => {
    if (!tournamentId || !enabled) return;
    let cancelled = false;
    apiFetch<{ round1: Round1AdminView }>(`/api/admin/shooting/tournaments/${tournamentId}/round1`)
      .then(async (body) => {
        if (cancelled) return;
        if (body.round1.teams.length > 0 || detail?.tournament.status === 'final') {
          setView(body.round1);
          return;
        }
        const ensured = await apiFetch<{ round1: Round1AdminView }>(
          `/api/admin/shooting/tournaments/${tournamentId}/round1/ensure`,
          { method: 'POST' },
        );
        if (!cancelled) setView(ensured.round1);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Could not load teams.');
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId, enabled, detail?.tournament.status]);

  async function refresh(next: Round1AdminView, toast?: string) {
    setView(next);
    if (toast) pushScoringToast(toast);
  }

  if (notFound) {
    return (
      <Layout>
        <p className="p-4 text-sm">This tournament was not found.</p>
      </Layout>
    );
  }

  if (detail?.tournament.status === 'final') return <FinalTournamentView detail={detail} />;

  return (
    <Layout>
      <div className="mx-auto max-w-3xl space-y-4 px-3 py-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Round 1 teams</h1>
          <Link to={scoringListPath(slug)} className="min-h-11 text-sm font-medium text-[var(--color-accent)]">
            Back to scores
          </Link>
        </div>
        {detail ? (
          <EventDayBar
            slug={slug}
            place="teams"
            isAdmin
            eventDay={eventDay}
            toggleBusy={busy}
            onToggle={() => void toggle()}
            counts={{ checkedIn: null, total: detail.shooters.length, shootingNow: null, scoresEntered: 0, waiting: null }}
            toggleError={dayError}
          />
        ) : null}
        {error ? <p className="text-sm text-[var(--color-error-text)]">{error}</p> : null}
        {loadError ? <p className="text-sm text-[var(--color-error-text)]">{loadError}</p> : null}
        {detail && !enabled ? <p className="text-sm text-[var(--color-text-secondary)]">Teams are only used for the rifle tournament.</p> : null}
        {view && tournamentId ? (
          <>
            <SettingsCard tournamentId={tournamentId} view={view} onSaved={(next) => void refresh(next, 'Settings saved')} />
            {view.tie ? <TieCard key={JSON.stringify([tournamentId, view.tie.slots, view.tie.teams.map((team) => team.teamId), view.tie.selectedTeamIds])} tournamentId={tournamentId} tie={view.tie} onSaved={(next) => void refresh(next, 'Round 2 teams updated')} /> : null}
            <TeamBoard view={view} />
            <section className="space-y-3" aria-label="Round 1 teams">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-base font-semibold">Teams</h2>
                <button
                  type="button"
                  className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-3 text-sm font-medium"
                  onClick={() => {
                    void apiFetch<{ round1: Round1AdminView }>(`/api/admin/shooting/tournaments/${tournamentId}/round1/teams`, {
                      method: 'POST',
                      body: JSON.stringify({}),
                    }).then((body) => refresh(body.round1, 'Team added'));
                  }}
                >
                  Add team
                </button>
              </div>
              {view.teams.map((team) => (
                <article key={team.teamId} className="space-y-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] p-3">
                  {rename?.id === team.teamId ? (
                    <form
                      className="flex gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void apiFetch<{ round1: Round1AdminView }>(
                          `/api/admin/shooting/tournaments/${tournamentId}/round1/teams/${team.teamId}`,
                          { method: 'PATCH', body: JSON.stringify({ name: rename.name }) },
                        ).then((body) => {
                          setRename(null);
                          return refresh(body.round1, 'Team renamed');
                        });
                      }}
                    >
                      <input className={fieldClass} value={rename.name} onChange={(event) => setRename({ id: team.teamId, name: event.target.value })} aria-label={`Name for ${team.name}`} />
                      <button type="submit" className="min-h-12 rounded-[var(--radius-md)] bg-[var(--color-accent)] px-3 text-sm font-medium text-white">
                        Save
                      </button>
                    </form>
                  ) : (
                    <button type="button" className="min-h-11 text-left text-base font-semibold" onClick={() => setRename({ id: team.teamId, name: team.name })}>
                      {team.name}
                      <span className="ml-2 text-sm font-normal text-[var(--color-text-tertiary)]">Tap to rename · {team.memberCount}</span>
                    </button>
                  )}
                  <ul className="space-y-2">
                    {team.members.map((member) => (
                      <MemberRow key={member.shooterId} tournamentId={tournamentId} member={member} teams={view.teams} onSaved={setView} />
                    ))}
                    {team.members.length === 0 ? <li className="text-sm text-[var(--color-text-tertiary)]">No shooters yet.</li> : null}
                  </ul>
                </article>
              ))}
            </section>
            <section aria-label="Unassigned shooters" className="space-y-2">
              <h2 className="text-base font-semibold">Unassigned</h2>
              {view.unassigned.length === 0 ? (
                <p className="text-sm text-[var(--color-text-secondary)]">Everyone is on a team.</p>
              ) : (
                <ul className="space-y-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] p-3">
                  {view.unassigned.map((member) => (
                    <MemberRow key={member.shooterId} tournamentId={tournamentId} member={member} teams={view.teams} onSaved={setView} />
                  ))}
                </ul>
              )}
            </section>
          </>
        ) : enabled && !loadError ? (
          <p className="text-sm text-[var(--color-text-secondary)]">Loading teams…</p>
        ) : null}
      </div>
    </Layout>
  );
}

function SettingsCard({
  tournamentId,
  view,
  onSaved,
}: {
  tournamentId: string;
  view: Round1AdminView;
  onSaved: (view: Round1AdminView) => void;
}) {
  const [teamSize, setTeamSize] = useState(String(view.teamSize));
  const [scoredShots, setScoredShots] = useState(String(view.scoredShots));
  const [qualifyingTeams, setQualifyingTeams] = useState(String(view.qualifyingTeams));
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const shotsChange = Number(scoredShots) !== view.scoredShots || Number(scoredShots) !== view.countedShots;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = await apiFetch<{ round1: Round1AdminView }>(`/api/admin/shooting/tournaments/${tournamentId}/round1/settings`, {
        method: 'PATCH',
        body: JSON.stringify({
          teamSize: Number(teamSize),
          scoredShots: Number(scoredShots),
          trialShots: view.trialShots,
          qualifyingTeams: Number(qualifyingTeams),
          ...(shotsChange ? { pin } : {}),
        }),
      });
      setPin('');
      onSaved(body.round1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save settings.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void save(event)} className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4" aria-label="Round 1 settings">
      <h2 className="text-base font-semibold">Settings</h2>
      {!view.allShotsCount ? (
        <p className="rounded-[var(--radius-md)] bg-amber-50 px-3 py-2 text-sm text-amber-950">
          Scored shots are still {view.scoredShots}, and the best {view.countedShots} count. Save scored shots as 10 so every shot counts. This needs the scoring PIN.
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <NumberField id="round1-team-size" label="Team size" value={teamSize} onChange={setTeamSize} hint="Default 6. Does not move anyone." />
        <NumberField id="round1-scored-shots" label="Scored shots" value={scoredShots} onChange={setScoredShots} hint="All of them count." />
        <NumberField id="round1-qualifying-teams" label="Teams in Round 2" value={qualifyingTeams} onChange={setQualifyingTeams} hint="Top teams, default 6." />
      </div>
      {shotsChange ? (
        <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
          PIN
          <input className={`${fieldClass} mt-1 text-center tracking-[0.3em]`} inputMode="numeric" type="password" value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))} aria-label="PIN" />
        </label>
      ) : null}
      {error ? <p className="text-sm text-[var(--color-error-text)]">{error}</p> : null}
      <button type="submit" disabled={busy} className="min-h-12 w-full rounded-[var(--radius-md)] bg-[var(--color-accent)] text-sm font-medium text-white disabled:opacity-50">
        {busy ? 'Saving…' : 'Save settings'}
      </button>
    </form>
  );
}

function NumberField({ id, label, value, onChange, hint }: { id: string; label: string; value: string; onChange: (value: string) => void; hint: string }) {
  return (
    <label className="block text-sm font-medium text-[var(--color-text-secondary)]" htmlFor={id}>
      {label}
      <input id={id} inputMode="numeric" value={value} onChange={(event) => onChange(event.target.value)} className={`${fieldClass} mt-1 tabular-nums`} />
      <span className="mt-1 block text-xs font-normal text-[var(--color-text-tertiary)]">{hint}</span>
    </label>
  );
}

function TieCard({
  tournamentId,
  tie,
  onSaved,
}: {
  tournamentId: string;
  tie: NonNullable<Round1AdminView['tie']>;
  onSaved: (view: Round1AdminView) => void;
}) {
  const selectedTeamIds = tie.selectedTeamIds ?? [];
  const [picked, setPicked] = useState<string[]>(selectedTeamIds);
  const [saving, setSaving] = useState(false);
  const resolved = selectedTeamIds.length === tie.slots;
  const [error, setError] = useState<string | null>(null);

  function toggle(teamId: string) {
    setPicked((current) => {
      if (current.includes(teamId)) return current.filter((id) => id !== teamId);
      if (current.length >= tie.slots) return current;
      return [...current, teamId];
    });
  }

  return (
    <section className="space-y-2 rounded-[var(--radius-lg)] border border-amber-300 bg-amber-50 p-4 text-amber-950" aria-label="Tied for Round 2">
      <h2 className="text-base font-semibold">Tied for the last Round 2 place</h2>
      <p className="text-sm">These teams are level on total and on shots of 10. Choose {tie.slots}. Order does not decide it.</p>
      {resolved ? <p className="text-sm">The saved choice is selected below. To change it, deselect a team and choose its replacement, then save. Existing Round 2 scores are kept; only qualifying teams appear in active results.</p> : null}
      <ul className="space-y-1">
        {tie.teams.map((team) => (
          <li key={team.teamId}>
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input type="checkbox" disabled={saving} checked={picked.includes(team.teamId)} onChange={() => toggle(team.teamId)} />
              <span>
                {team.name} · {team.totalDisplay} · {team.tens} tens
              </span>
            </label>
          </li>
        ))}
      </ul>
      {error ? <p className="text-sm">{error}</p> : null}
      <button
        type="button"
        className="min-h-12 w-full rounded-[var(--radius-md)] bg-[var(--color-accent)] text-sm font-medium text-white disabled:opacity-50"
        disabled={saving || picked.length !== tie.slots || (resolved && picked.every((id) => selectedTeamIds.includes(id)))}
        onClick={() => {
          setError(null);
          setSaving(true);
          void apiFetch<{ round1: Round1AdminView }>(`/api/admin/shooting/tournaments/${tournamentId}/round1/tie-break`, {
            method: 'POST',
            body: JSON.stringify({ teamIds: picked }),
          })
            .then((body) => onSaved(body.round1))
            .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Could not save that choice.'))
            .finally(() => setSaving(false));
        }}
      >
        {saving ? 'Saving…' : resolved ? 'Update Round 2 choice' : 'Send these teams to Round 2'}
      </button>
    </section>
  );
}

function TeamBoard({ view }: { view: Round1AdminView }) {
  const tied = new Set(view.tie?.teams.map((team) => team.teamId) ?? []);
  const tiedAt = view.teams.findIndex((team) => tied.has(team.teamId));
  const unresolved = view.tie && (view.tie.selectedTeamIds?.length ?? 0) !== view.tie.slots;
  const cutAt = unresolved ? (tiedAt === -1 ? view.qualifyingTeams : tiedAt) : view.qualifyingTeams;
  return (
    <section aria-label="Team leaderboard" className="space-y-2">
      <h2 className="text-base font-semibold">Team leaderboard</h2>
      <p className="text-sm text-[var(--color-text-secondary)]">
        Sum of every member’s Round 1 total. The line is after team {view.qualifyingTeams}. Men and women stay as separate rankings, with no cut.
      </p>
      <ol className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)]">
        {view.teams.map((team, index) => (
          <React.Fragment key={team.teamId}>
            {index === cutAt && cutAt > 0 && cutAt < view.teams.length ? (
              <li className="bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-950">▲ Round 2 teams</li>
            ) : null}
            <li className="flex items-baseline justify-between gap-3 border-t border-[var(--color-border)] px-3 py-2 first:border-t-0">
              <span>
                <span className="tabular-nums text-[var(--color-text-tertiary)]">{team.rank ? `${team.rank}.` : '—'} </span>
                {team.name}
                <span className="block text-xs text-[var(--color-text-tertiary)]">
                  {team.memberCount} shooters · {team.tens} tens
                </span>
              </span>
              <span className="text-lg font-semibold tabular-nums">{team.totalDisplay}</span>
            </li>
          </React.Fragment>
        ))}
      </ol>
    </section>
  );
}

function MemberRow({
  tournamentId,
  member,
  teams,
  onSaved,
}: {
  tournamentId: string;
  member: { shooterId: string; name: string; bib: string | null; gender: Round1AdminView['unassigned'][number]['gender']; totalDisplay: string };
  teams: Round1AdminView['teams'];
  onSaved: (view: Round1AdminView) => void;
}) {
  const teamId = teams.find((team) => team.members.some((row) => row.shooterId === member.shooterId))?.teamId ?? '';
  return (
    <li className="flex flex-wrap items-center justify-between gap-2">
      <span className="min-w-0 text-sm">
        {member.name}
        <span className="text-[var(--color-text-tertiary)]">
          {' '}
          · {member.bib ?? 'No number'}
          {member.gender ? ` · ${SHOOTER_GENDER_LABELS[member.gender]}` : ''} · {member.totalDisplay}
        </span>
      </span>
      <select
        aria-label={`Team for ${member.name}`}
        className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] px-2 text-sm"
        value={teamId}
        onChange={(event) => {
          const next = event.target.value;
          void apiFetch<{ round1: Round1AdminView }>(
            `/api/admin/shooting/tournaments/${tournamentId}/round1/members/${member.shooterId}`,
            { method: 'PATCH', body: JSON.stringify({ teamId: next || null }) },
          ).then((body) => onSaved(body.round1));
        }}
      >
        <option value="">Unassigned</option>
        {teams.map((team) => (
          <option key={team.teamId} value={team.teamId}>
            {team.name}
          </option>
        ))}
      </select>
    </li>
  );
}
